import {Stack} from '@mui/material';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useEffect, useState} from 'react';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {translations} from '../../../../../../i18n/translations';
import {useAuth} from '../../../../../../contexts/auth/context';
import {PendingTasks} from './pending-tasks';
import {UsersFilters} from '../../../../../../components/users-filters';
import {UsersInput} from '../../../../../../components/users-input/users-input';
import {PendingTasksModal} from './modals/pending-tasks-modal';
import {PendingTask} from './format-pending-task';
import {EntityUserCareTasksRepository} from '../../../../../../repositories/entity-user-care-tasks';
import moment from 'moment';
import {CareTask} from '../../../../../../backend/interfaces/care-task';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {CareTaskComplementary} from '../../../../../../backend/interfaces/care-task-complementary';
import {HandleCareTaskModal} from '../../../../../../components/task-table/modals/handle-task';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../../../../../utils/constants';
import {useEntity} from '../../../../../../contexts/entity/context';
import './pending-tasks.professional-caregiver.screen.scss';

type LocationState = {
  from: string;
};

export function PendingTasksProfessionalCaregiverOfficeScreen() {
  const {t} = useTranslation();
  const {
    state: {session},
  } = useAuth();
  const location = useLocation();
  let history = useHistory();
  const {
    updateEntityPendingTasks,
    state: {pendingTasks},
  } = useEntity();
  const snackBarContext = useSnackBar();
  const {hasWritePermission} = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [locationState] = useState<LocationState | undefined>(location.state as LocationState);
  const [filters, setRawFilters] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [allValidatingTasks, setAllValidatingTasks] = useState<
    {[id: string]: PendingTask} | undefined
  >(undefined);
  const [validatingTasks, setValidatingTasks] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isModalTaskOpen, setIsModalTaskOpen] = useState(false);
  const [modalTask, setModalTask] = useState<CareTaskComplementary>();
  const [canWriteModalTaskSector, setCanWriteModalTaskSector] = useState(false);

  useEffect(() => {
    if (modalTask) {
      if (
        (hasWritePermission(RolePermissionType.Calendar_BADL) &&
          modalTask?.careTask.activitiesSectorId === BADL_SECTOR_ID) ||
        (hasWritePermission(RolePermissionType.Calendar_IADL) &&
          modalTask?.careTask.activitiesSectorId === IADL_SECTOR_ID) ||
        (hasWritePermission(RolePermissionType.Calendar_ClinicalSupport) &&
          modalTask?.careTask.activitiesSectorId === CLINICAL_SUPPORT_SECTOR_ID) ||
        (hasWritePermission(RolePermissionType.Calendar_PersonalDevelopment) &&
          modalTask?.careTask.activitiesSectorId === PERSONAL_DEVELOPMENT_SECTOR_ID)
      ) {
        setCanWriteModalTaskSector(true);
      } else {
        setCanWriteModalTaskSector(false);
      }
    }
  }, [hasWritePermission, modalTask]);

  useEffect(
    () => {
      const fetchData = async () => {
        await updateEntityPendingTasks();
        setIsLoading(false);
      };
      if (isLoading) fetchData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  );

  const handleCancel = () => {
    if (locationState?.from) {
      history.push(locationState.from);
    }
  };

  const handleValidateTasks = async (observation: string) => {
    if (!validatingTasks) return;

    setIsValidating(true);
    const requests: Promise<CareTask>[] = [];
    validatingTasks.forEach((taskId) => {
      const task = allValidatingTasks?.[taskId];
      if (!task || !session?.selectedAffiliation) return;
      requests.push(
        EntityUserCareTasksRepository.instance.HandleCareTask(
          session.selectedAffiliation.entity.id,
          task.userId,
          task.ref,
          {
            markedAsDone: true,
            executionDatetime: moment().toISOString(),
            notes: observation ? observation : null,
          }
        )
      );
    });
    try {
      await Promise.all(requests);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.office.pending_tasks.snackbar_success),
      });

      setIsValidating(false);
      setAllValidatingTasks(undefined);
      setValidatingTasks([]);

      if (locationState?.from) {
        history.push(locationState.from);
      } else {
        await updateEntityPendingTasks();
      }
    } catch (e) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.office.pending_tasks.snackbar_error),
      });
      setIsValidating(false);
      setAllValidatingTasks(undefined);
      setValidatingTasks([]);
    }
  };

  const handlePendingClick = async (pendingId: string) => {
    const task = pendingTasks.find((task) => task.pendingCareTask.id === pendingId);

    if (task && session?.selectedAffiliation) {
      setIsModalTaskOpen(true);
      try {
        const careTask = await EntityUserCareTasksRepository.instance.GetEntityCareTask(
          session.selectedAffiliation.entity.id,
          task.careTask.ref
        );
        setModalTask(careTask);
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <Stack direction="column" sx={{gap: '15px'}}>
      <GenericHeader
        pageTitle={t(translations.office.pending_tasks.header_title)}
        isLoading={false}
        leftColumn={
          <Stack gap={2} sx={{display: 'flex', flexDirection: 'row', padding: 0, width: '100%'}}>
            <UsersInput onChange={(user) => setUserFilter(user?.user.id || '')} />
            <UsersFilters
              hideActiveStatus
              hideTaskGroups
              hideOccurrences
              onChange={setRawFilters}
            />
          </Stack>
        }
      />
      <PendingTasks
        loading={isLoading}
        data={pendingTasks}
        onCancel={handleCancel}
        onValidateAllTasks={(tasks, selected) => {
          setAllValidatingTasks(tasks);
          setValidatingTasks(selected);
        }}
        from={locationState?.from || ''}
        rawFilters={filters}
        userFilter={userFilter}
        onPendingClick={handlePendingClick}
        canWrite={{
          calendar: hasWritePermission(RolePermissionType.Calendar),
          iadl: hasWritePermission(RolePermissionType.Calendar_IADL),
          badl: hasWritePermission(RolePermissionType.Calendar_BADL),
          clinicalSupport: hasWritePermission(RolePermissionType.Calendar_ClinicalSupport),
          personalDevelopment: hasWritePermission(RolePermissionType.Calendar_PersonalDevelopment),
        }}
      />
      {allValidatingTasks && (
        <PendingTasksModal
          open
          submitting={isValidating}
          tasks={validatingTasks}
          onValidate={handleValidateTasks}
          onClose={() => {
            setAllValidatingTasks(undefined);
            setValidatingTasks([]);
          }}
        />
      )}
      {isModalTaskOpen && (
        <HandleCareTaskModal
          modalTask={modalTask}
          careTasks={[]}
          setCareTasks={async () => {
            await updateEntityPendingTasks();
            setIsModalTaskOpen(false);
            setModalTask(undefined);
          }}
          open
          onClose={() => {
            setIsModalTaskOpen(false);
            setModalTask(undefined);
          }}
          userId={modalTask?.careTask.userId || ''}
          canWrite={hasWritePermission(RolePermissionType.Calendar) && canWriteModalTaskSector}
          displayPendingTime
        />
      )}
    </Stack>
  );
}
